'use client';

import { useSyncExternalStore } from 'react';

/** Guest read state (persists across visits). */
export const NEWS_READ_IDS_KEY = 'news:read-ids';
/** Ids read while logged out, queued for account sync on login (tab session only). */
export const NEWS_PENDING_SYNC_IDS_KEY = 'news:pending-sync-ids';

const emptyReadIds = new Set<string>();
const listeners = new Set<() => void>();
let snapshot: Set<string> = emptyReadIds;
/** When false (logged-in), pending-sync queue is not written until logout. */
let syncQueueEnabled = true;

const setsEqual = (a: Set<string>, b: Set<string>) => {
    if (a.size !== b.size) return false;
    for (const id of a) {
        if (!b.has(id)) return false;
    }
    return true;
};

const normalizeReadId = (id: unknown): string | null => {
    if (typeof id === 'string') {
        const trimmed = id.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof id === 'number' && Number.isFinite(id)) {
        return String(id);
    }
    return null;
};

const parseIdSet = (raw: string | null): Set<string> => {
    if (!raw) return emptyReadIds;
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return emptyReadIds;
        const ids = parsed.map(normalizeReadId).filter((id): id is string => id !== null);
        return ids.length > 0 ? new Set(ids) : emptyReadIds;
    } catch {
        return emptyReadIds;
    }
};

const readIdsFromLocalStorage = (): Set<string> => {
    if (typeof localStorage === 'undefined') return emptyReadIds;
    return parseIdSet(localStorage.getItem(NEWS_READ_IDS_KEY));
};

const getSnapshot = () => {
    const next = readIdsFromLocalStorage();
    if (setsEqual(next, snapshot)) return snapshot;
    snapshot = next;
    return snapshot;
};

const subscribe = (onStoreChange: () => void) => {
    listeners.add(onStoreChange);
    const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === NEWS_READ_IDS_KEY) {
            onStoreChange();
        }
    };
    if (typeof window !== 'undefined') {
        window.addEventListener('storage', onStorage);
    }
    return () => {
        listeners.delete(onStoreChange);
        if (typeof window !== 'undefined') {
            window.removeEventListener('storage', onStorage);
        }
    };
};

const notify = () => {
    listeners.forEach((listener) => listener());
};

export const setNewsReadSyncQueueEnabled = (enabled: boolean) => {
    syncQueueEnabled = enabled;
};

export const getPendingSyncReadIds = (): Set<string> => {
    if (typeof sessionStorage === 'undefined') return emptyReadIds;
    return parseIdSet(sessionStorage.getItem(NEWS_PENDING_SYNC_IDS_KEY));
};

export const clearPendingSyncReadIds = () => {
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(NEWS_PENDING_SYNC_IDS_KEY);
    }
};

const addPendingSyncReadId = (id: string) => {
    if (!syncQueueEnabled || typeof sessionStorage === 'undefined') return;
    const next = new Set(getPendingSyncReadIds());
    if (next.has(id)) return;
    next.add(id);
    sessionStorage.setItem(NEWS_PENDING_SYNC_IDS_KEY, JSON.stringify([...next]));
};

/** SSR-safe guest read ids for React subscribers (localStorage). */
export const useGuestNewsReadIds = () => useSyncExternalStore(subscribe, getSnapshot, () => emptyReadIds);

/** Imperative read (effects, mark-read); triggers subscriber updates when storage changes. */
export const getNewsReadIds = () => getSnapshot();

/** Mark-read: always localStorage; session sync queue only while logged out. */
export const addNewsReadId = (id: string) => {
    if (typeof localStorage === 'undefined') return;
    const normalized = normalizeReadId(id);
    if (!normalized) return;
    const next = new Set(getSnapshot());
    if (!next.has(normalized)) {
        next.add(normalized);
        localStorage.setItem(NEWS_READ_IDS_KEY, JSON.stringify([...next]));
        snapshot = next;
        notify();
    }
    addPendingSyncReadId(normalized);
};
